import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp 
} from 'firebase/firestore'
import { db } from '@/firebase/config'

export class CommunityService {
  constructor() {
    this.postsRef = collection(db, 'posts')
    this.usersRef = collection(db, 'users')
    this.achievementsRef = collection(db, 'achievements')
    this.videosRef = collection(db, 'healthVideos')
  }

  // Create a new post
  async createPost(userId, postData) {
    try {
      // Get user information to save author name
      let authorName = 'Unknown User'
      
      try {
        const userDoc = await getDoc(doc(db, 'users', userId))
        if (userDoc.exists()) {
          const userData = userDoc.data()
          authorName = userData.displayName || 
                      userData.email?.split('@')[0] || 
                      userData.name || 
                      'Unknown User'
        }
      } catch (userError) {
        console.warn('Could not fetch user data for authorName:', userError)
        // Continue with 'Unknown User' as fallback
      }
      
      
      const docRef = await addDoc(this.postsRef, {
        ...postData,
        authorId: userId,
        authorName: authorName, // Save author name directly
        createdAt: serverTimestamp(),
        likes: [],
        comments: [],
        ratings: [],
        averageRating: 0,
        totalRatings: 0,
        views: 0
      })
      
      return { success: true, postId: docRef.id, authorName: authorName }
    } catch (error) {
      console.error('Error creating post:', error)
      return { success: false, error: error.message }
    }
  }

  // Get a single post by ID
  async getPostById(postId) {
    try {
      const postRef = doc(db, 'posts', postId)
      const postSnap = await getDoc(postRef)

      if (!postSnap.exists()) {
        return { success: false, error: 'Post not found' }
      }

      const postData = postSnap.data()

      // Get author information
      const authorDoc = await getDoc(doc(db, 'users', postData.authorId))
      const authorData = authorDoc.exists() ? authorDoc.data() : { displayName: 'Unknown User' }

      // Handle createdAt - could be Timestamp or string
      let createdAtISO
      if (postData.createdAt) {
        if (typeof postData.createdAt === 'string') {
          createdAtISO = postData.createdAt
        } else if (postData.createdAt.toDate && typeof postData.createdAt.toDate === 'function') {
          createdAtISO = postData.createdAt.toDate().toISOString()
        } else {
          createdAtISO = new Date().toISOString()
        }
      } else {
        createdAtISO = new Date().toISOString()
      }

      const post = {
        id: postSnap.id,
        ...postData,
        authorName: postData.authorName || authorData.displayName || authorData.email?.split('@')[0] || 'Unknown User',
        author: {
          name: postData.authorName || authorData.displayName || authorData.email?.split('@')[0] || 'Unknown User',
          avatar: authorData.profile?.avatar || '',
          role: authorData.role || 'user'
        },
        createdAt: createdAtISO,
        averageRating: postData.averageRating || 0,
        totalRatings: postData.totalRatings || 0,
        ratings: postData.ratings || [],
        views: postData.views || 0,
        comments: postData.comments || [],
        likes: postData.likes || []
      }

      return { success: true, post }
    } catch (error) {
      console.error('Error getting post by ID:', error)
      return { success: false, error: error.message }
    }
  }

  // Get posts list with optional topic filter
  async getPosts(topicFilter = null, limitCount = 100) {
    try {
      // Topic mapping: UI uses IDs, but posts in DB use full names
      const topicMap = {
        'pregnancy': 'Pregnancy & Parenting',
        'weight-loss': 'Weight Loss Tips',
        'health': 'Physical Health',
        'stress': 'Stress Relief',
        'beauty': 'Beauty & Care'
      }

      // Get all posts and filter in memory to handle both naming conventions
      let q = query(this.postsRef, orderBy('createdAt', 'desc'), limit(limitCount))
      const querySnapshot = await getDocs(q)
      const posts = []

      for (const docSnap of querySnapshot.docs) {
        const postData = docSnap.data()

        // Skip if filtering by topic and post doesn't match
        if (topicFilter) {
          const topicFullName = topicMap[topicFilter] || topicFilter
          // Check both the ID and full name for compatibility
          if (postData.topic !== topicFilter && postData.topic !== topicFullName) {
            continue
          }
        }

        // Get author information
        const authorDoc = await getDoc(doc(db, 'users', postData.authorId))
        const authorData = authorDoc.exists() ? authorDoc.data() : { displayName: 'Unknown User' }

        // Handle createdAt - could be Timestamp or string
        let createdAtISO
        if (postData.createdAt) {
          if (typeof postData.createdAt === 'string') {
            createdAtISO = postData.createdAt
          } else if (postData.createdAt.toDate && typeof postData.createdAt.toDate === 'function') {
            createdAtISO = postData.createdAt.toDate().toISOString()
          } else {
            createdAtISO = new Date().toISOString()
          }
        } else {
          createdAtISO = new Date().toISOString()
        }

        posts.push({
          id: docSnap.id,
          ...postData,
          // Normalize topic to ID format for UI consistency
          topic: this.normalizeTopicToId(postData.topic),
          // Use saved authorName as primary, fallback to user data lookup
          authorName: postData.authorName || authorData.displayName || authorData.email?.split('@')[0] || 'Unknown User',
          author: {
            name: postData.authorName || authorData.displayName || authorData.email?.split('@')[0] || 'Unknown User',
            avatar: authorData.profile?.avatar || '',
            role: authorData.role || 'user'
          },
          createdAt: createdAtISO,
          // Ensure rating fields have default values
          averageRating: postData.averageRating || 0,
          totalRatings: postData.totalRatings || 0,
          ratings: postData.ratings || [],
          views: postData.views || 0,
          // Ensure comment fields have default values
          comments: postData.comments || [],
          likes: postData.likes || []
        })
      }

      return { success: true, posts }
    } catch (error) {
      console.error('Error getting posts:', error)
      return { success: false, error: error.message, posts: [] }
    }
  }

  // Helper method to normalize topic names to IDs
  normalizeTopicToId(topic) {
    const nameToIdMap = {
      'Pregnancy & Parenting': 'pregnancy',
      'Weight Loss Tips': 'weight-loss',
      'Physical Health': 'health',
      'Stress Relief': 'stress',
      'Beauty & Care': 'beauty'
    }
    return nameToIdMap[topic] || topic
  }

  // Like a post
  async likePost(postId, userId) {
    try {
      const postRef = doc(db, 'posts', postId)
      await updateDoc(postRef, {
        likes: arrayUnion(userId)
      })
      return { success: true }
    } catch (error) {
      console.error('Error liking post:', error)
      return { success: false, error: error.message }
    }
  }

  // Unlike a post
  async unlikePost(postId, userId) {
    try {
      const postRef = doc(db, 'posts', postId)
      await updateDoc(postRef, {
        likes: arrayRemove(userId)
      })
      return { success: true }
    } catch (error) {
      console.error('Error unliking post:', error)
      return { success: false, error: error.message }
    }
  }

  // Rate a post
  async ratePost(postId, userId, rating, review = '') {
    try {
      const postRef = doc(db, 'posts', postId)
      const postSnap = await getDoc(postRef)
      
      if (!postSnap.exists()) {
        console.error('Post not found:', postId)
        return { success: false, error: 'Post not found' }
      }
      
      const postData = postSnap.data()
      const existingRatings = postData.ratings || []
      
      // Check if user has already rated
      const existingRatingIndex = existingRatings.findIndex(r => r.userId === userId)
      
      let newRatings = [...existingRatings]
      let totalRatings = postData.totalRatings || 0
      let ratingSum = (postData.averageRating || 0) * totalRatings
      
      if (existingRatingIndex >= 0) {
        // Update existing rating
        const oldRating = existingRatings[existingRatingIndex].rating
        ratingSum = ratingSum - oldRating + rating
        newRatings[existingRatingIndex] = {
          userId,
          rating,
          review,
          createdAt: new Date()
        }
      } else {
        // Add new rating
        ratingSum += rating
        totalRatings += 1
        newRatings.push({
          userId,
          rating,
          review,
          createdAt: new Date()
        })
      }
      
      const averageRating = totalRatings > 0 ? ratingSum / totalRatings : 0
      
      await updateDoc(postRef, {
        ratings: newRatings,
        averageRating: Number(averageRating.toFixed(1)),
        totalRatings
      })

      return { success: true, averageRating: Number(averageRating.toFixed(1)), totalRatings }
    } catch (error) {
      console.error('Error rating post:', error)
      return { success: false, error: error.message }
    }
  }

  // Add comment to post
  async addComment(postId, userId, content) {
    try {
      const postRef = doc(db, 'posts', postId)
      const userDoc = await getDoc(doc(db, 'users', userId))
      const userData = userDoc.exists() ? userDoc.data() : {}
      
      const comment = {
        id: Date.now().toString(),
        userId,
        author: userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
        content,
        createdAt: new Date() // Using new Date() instead of serverTimestamp for consistency
      }

      await updateDoc(postRef, {
        comments: arrayUnion(comment)
      })

      return { success: true, comment }
    } catch (error) {
      console.error('Error adding comment:', error)
      return { success: false, error: error.message }
    }
  }

  // Increment post views
  async incrementViews(postId) {
    try {
      const postRef = doc(db, 'posts', postId)
      await updateDoc(postRef, {
        views: increment(1)
      })
      return { success: true }
    } catch (error) {
      console.error('Error incrementing views:', error)
      return { success: false, error: error.message }
    }
  }

  // Get user achievements
  async getUserAchievements(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        return {
          success: true,
          achievements: userData.achievements || {
            points: 0,
            badges: [],
            level: 1,
            posts: 0,
            likes: 0,
            comments: 0
          }
        }
      }
      return { success: false, error: 'User not found' }
    } catch (error) {
      console.error('Error getting user achievements:', error)
      return { success: false, error: error.message }
    }
  }

  // Update user achievements
  async updateUserAchievements(userId, actionType) {
    try {
      const userRef = doc(db, 'users', userId)
      const userSnap = await getDoc(userRef)
      
      if (!userSnap.exists()) {
        return { success: false, error: 'User not found' }
      }
      
      const userData = userSnap.data()
      const achievements = userData.achievements || {
        points: 0,
        badges: [],
        level: 1,
        posts: 0,
        likes: 0,
        comments: 0
      }
      
      // Add points and stats based on action type
      const pointsMap = {
        'post': 10,
        'like': 2,
        'comment': 5,
        'rate': 3
      }
      
      achievements.points += pointsMap[actionType] || 0
      achievements[actionType + 's'] = (achievements[actionType + 's'] || 0) + 1
      
      // Calculate level (one level per 100 points)
      achievements.level = Math.floor(achievements.points / 100) + 1
      
      // Check and add badges
      const newBadges = []
      if (achievements.posts >= 10 && !achievements.badges.includes('Active Poster')) {
        newBadges.push('Active Poster')
      }
      if (achievements.points >= 500 && !achievements.badges.includes('Community Star')) {
        newBadges.push('Community Star')
      }
      if (achievements.likes >= 50 && !achievements.badges.includes('Popular')) {
        newBadges.push('Popular')
      }
      
      achievements.badges = [...achievements.badges, ...newBadges]
      
      await updateDoc(userRef, { achievements })
      
      return { success: true, achievements, newBadges }
    } catch (error) {
      console.error('Error updating achievements:', error)
      return { success: false, error: error.message }
    }
  }

  // Get health education videos
  async getHealthVideos(category = null) {
    try {
      let q = query(this.videosRef, orderBy('createdAt', 'desc'))
      
      if (category) {
        q = query(this.videosRef, where('category', '==', category), orderBy('createdAt', 'desc'))
      }
      
      const querySnapshot = await getDocs(q)
      const videos = []
      
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        videos.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate().toISOString()
        })
      })
      
      return { success: true, videos }
    } catch (error) {
      console.error('Error getting videos:', error)
      return { success: false, error: error.message, videos: [] }
    }
  }

  // Add health education video (admin function)
  async addHealthVideo(videoData) {
    try {
      const docRef = await addDoc(this.videosRef, {
        ...videoData,
        createdAt: serverTimestamp(),
        views: 0,
        ratings: [],
        averageRating: 0
      })
      return { success: true, videoId: docRef.id }
    } catch (error) {
      console.error('Error adding video:', error)
      return { success: false, error: error.message }
    }
  }

  // Seed demo data for development
  async seedDemoData() {
    try {
      // Sample users
      const sampleUsers = [
        {
          id: 'sarah-johnson-123',
          email: 'sarah.johnson@email.com',
          displayName: 'Sarah Johnson',
          role: 'user',
          status: 'active',
          createdAt: new Date().toISOString(),
          profile: {
            bio: 'Fitness enthusiast and new mom sharing my wellness journey',
            avatar: '',
            phone: '',
            dateOfBirth: '1990-05-15',
            address: 'Melbourne, Australia'
          }
        },
        {
          id: 'emma-chen-456',
          email: 'emma.chen@email.com',
          displayName: 'Emma Chen',
          role: 'user',
          status: 'active',
          createdAt: new Date().toISOString(),
          profile: {
            bio: 'Nutritionist passionate about women\'s health and balanced living',
            avatar: '',
            phone: '',
            dateOfBirth: '1988-09-22',
            address: 'Sydney, Australia'
          }
        },
        {
          id: 'maria-garcia-789',
          email: 'maria.garcia@email.com',
          displayName: 'Maria Garcia',
          role: 'user',
          status: 'active',
          createdAt: new Date().toISOString(),
          profile: {
            bio: 'Yoga instructor and mindfulness advocate',
            avatar: '',
            phone: '',
            dateOfBirth: '1992-03-10',
            address: 'Brisbane, Australia'
          }
        },
        {
          id: 'jessica-kim-101',
          email: 'jessica.kim@email.com',
          displayName: 'Jessica Kim',
          role: 'user',
          status: 'active',
          createdAt: new Date().toISOString(),
          profile: {
            bio: 'Working professional focused on work-life balance and mental health',
            avatar: '',
            phone: '',
            dateOfBirth: '1991-11-07',
            address: 'Perth, Australia'
          }
        },
        {
          id: 'priya-patel-112',
          email: 'priya.patel@email.com',
          displayName: 'Priya Patel',
          role: 'user',
          status: 'active',
          createdAt: new Date().toISOString(),
          profile: {
            bio: 'Medical student and health research enthusiast',
            avatar: '',
            phone: '',
            dateOfBirth: '1995-07-30',
            address: 'Adelaide, Australia'
          }
        }
      ]

      // Sample posts
      const samplePosts = [
        {
          authorId: 'sarah-johnson-123',
          authorName: 'Sarah Johnson',
          title: 'My Post-Pregnancy Fitness Journey',
          content: `It's been 6 months since I gave birth to my beautiful daughter, and I wanted to share my experience getting back into fitness. 

The first few weeks were all about rest and recovery. I focused on gentle walks and basic breathing exercises. Around week 8, I started with postnatal yoga and pelvic floor strengthening.

Now at 6 months, I'm doing 3-4 workouts per week including:
- 2x strength training sessions (modified for new mom schedule)
- 1x yoga class
- Daily walks with the pram

Key lessons learned:
1. Listen to your body - some days you need rest
2. Start slowly and build up gradually
3. Find workouts you can do at home
4. Community support makes all the difference

Anyone else on a similar journey? Would love to connect!`,
          topic: 'pregnancy',
          tags: ['postpartum', 'fitness', 'new-mom', 'recovery'],
          type: 'experience'
        },
        {
          authorId: 'emma-chen-456',
          authorName: 'Emma Chen',
          title: 'Nutrition Tips for Busy Women',
          content: `As a nutritionist, I see so many women struggling to eat well due to busy schedules. Here are my top 5 practical tips:

1. **Meal Prep Sundays**: Spend 2 hours preparing basics like roasted vegetables, cooked grains, and proteins.

2. **Healthy Snack Stash**: Keep nuts, fruits, yogurt, and veggie sticks readily available.

3. **One-Bowl Meals**: Buddha bowls are nutritious and can be made in 10 minutes with prepped ingredients.

4. **Hydration First**: Start your day with a large glass of water. Dehydration often masquerades as hunger.

5. **80/20 Rule**: Aim for nutritious choices 80% of the time, allow flexibility for the remaining 20%.

Remember: perfection isn't the goal, consistency is! What's your biggest nutrition challenge?`,
          topic: 'weight-loss',
          tags: ['nutrition', 'meal-prep', 'busy-lifestyle', 'healthy-eating'],
          type: 'advice'
        },
        {
          authorId: 'maria-garcia-789',
          authorName: 'Maria Garcia',
          title: 'Morning Yoga Routine for Stress Relief',
          content: `Starting your day with yoga can completely transform your stress levels. Here's my favorite 15-minute morning sequence:

**5-minute breathing** (Pranayama):
- 4-7-8 breathing technique
- Helps activate the parasympathetic nervous system

**8-minute gentle flow**:
- Cat-cow stretches (2 minutes)
- Downward dog to child's pose flow (3 minutes)
- Seated spinal twists (2 minutes)
- Legs up the wall pose (1 minute)

**2-minute meditation**:
- Set intention for the day
- Practice gratitude

The key is consistency, not perfection. Even 5 minutes makes a difference! I've been doing this routine for 3 years and it's changed my relationship with stress completely.

Who wants to join me for a virtual morning yoga session?`,
          topic: 'stress',
          tags: ['yoga', 'stress-relief', 'morning-routine', 'meditation'],
          type: 'routine'
        },
        {
          authorId: 'jessica-kim-101',
          authorName: 'Jessica Kim',
          title: 'Balancing Career and Self-Care',
          content: `Corporate life can be draining, especially for women juggling multiple responsibilities. After experiencing burnout last year, I've learned some valuable lessons about maintaining balance:

**Boundaries are non-negotiable:**
- No emails after 7 PM
- Lunch breaks are sacred (even if it's just 20 minutes)
- Weekend work is emergency-only

**Micro self-care moments:**
- 5-minute desk stretches every hour
- Mindful coffee breaks
- Gratitude journaling before bed

**Support systems:**
- Regular check-ins with friends
- Professional therapy when needed
- Joining communities like this one!

The result? Better work performance, improved relationships, and actually enjoying life again. What's your biggest work-life balance challenge?`,
          topic: 'stress',
          tags: ['work-life-balance', 'self-care', 'burnout-prevention', 'career'],
          type: 'experience'
        },
        {
          authorId: 'priya-patel-112',
          authorName: 'Priya Patel',
          title: 'Understanding PCOS: My Research and Personal Experience',
          content: `As a medical student diagnosed with PCOS, I want to share what I've learned both academically and personally.

**What is PCOS?**
Polycystic Ovary Syndrome affects 1 in 10 women of reproductive age. It's a hormonal disorder causing irregular periods, excess androgens, and polycystic ovaries.

**Common symptoms:**
- Irregular or absent periods
- Weight gain or difficulty losing weight
- Acne and excess hair growth
- Hair thinning
- Mood changes

**Management strategies that work for me:**
1. **Diet**: Low GI foods, reduced refined sugars
2. **Exercise**: Combination of cardio and strength training
3. **Supplements**: Inositol, Vitamin D (consult your doctor first)
4. **Sleep**: 7-8 hours consistently
5. **Stress management**: Meditation and journaling

**Important note**: PCOS presents differently in everyone. What works for me might not work for you. Always consult healthcare professionals for personalized advice.

Anyone else managing PCOS? Let's support each other!`,
          topic: 'health',
          tags: ['PCOS', 'hormonal-health', 'medical-research', 'lifestyle-management'],
          type: 'educational'
        },
        {
          authorId: 'emma-chen-456',
          authorName: 'Emma Chen',
          title: 'Plant-Based Protein Sources for Women',
          content: `Many women worry about getting enough protein on plant-based diets. Here's the truth: it's totally achievable!

**Top plant protein sources (per 100g):**
- Tempeh: 19g, Lentils: 9g, Chickpeas: 8g, Quinoa: 4.4g
- Chia seeds: 17g, Hemp seeds: 31g, Spirulina: 57g

**My favorite protein-rich recipes:**
1. **Buddha Bowl**: Quinoa + chickpeas + tahini dressing = 25g protein
2. **Smoothie**: Plant milk + protein powder + chia seeds = 30g protein
3. **Lentil curry**: With brown rice = 18g protein per serving

Questions about plant-based nutrition? Ask away!`,
          topic: 'weight-loss',
          tags: ['plant-based', 'protein', 'nutrition', 'meal-planning'],
          type: 'educational'
        }
      ]

      // Create users first
      for (const user of sampleUsers) {
        try {
          await setDoc(doc(db, 'users', user.id), user)
        } catch (error) {
          console.error(`Error creating user ${user.displayName}:`, error)
        }
      }

      // Create posts
      for (const post of samplePosts) {
        try {
          await addDoc(this.postsRef, {
            ...post,
            createdAt: serverTimestamp(),
            likes: [],
            comments: [],
            ratings: [],
            averageRating: 0,
            totalRatings: 0,
            views: Math.floor(Math.random() * 50) + 5
          })
        } catch (error) {
          console.error(`Error creating post ${post.title}:`, error)
        }
      }

      return { success: true, message: 'Demo data created successfully' }
      
    } catch (error) {
      console.error('Error seeding demo data:', error)
      return { success: false, error: error.message }
    }
  }
}